import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    Check,
    Loader2,
    ShieldCheck,
    Sparkles,
    LogOut,
    CreditCard,
    PartyPopper,
    UserCircle,
} from "lucide-react";
import { NeonBackground } from "@/components/reelflo/NeonBackground";
import { NeonLogo } from "@/components/reelflo/NeonLogo";
import { NeonButton } from "@/components/reelflo/NeonButton";
import { useAuth } from "@/context/AuthContext";
import { hasPaidAccess, nextRouteForUser } from "@/lib/access";
import api from "@/lib/api";

const PERKS = [
    "Professional video edit",
    "Auto captions added",
    "Dead air & pauses removed",
    "Studio audio cleanup",
    "Trending music added",
    "Full transcript included",
    "Ready-to-post captions",
];

const POLL_MAX_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 2500;

export default function Subscribe() {
    const { user, loading, refreshUser, logout } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const sessionId = searchParams.get("session_id");
    const canceled = searchParams.get("canceled");

    // "idle" | "subscribing" | "verifying" | "success" | "error"
    const [phase, setPhase] = useState(sessionId ? "verifying" : "idle");
    const [errorMsg, setErrorMsg] = useState("");
    const pollRef = useRef({ attempts: 0, stopped: false });

    const config = useMemo(
        () => ({ amount: 299, currency: "USD", interval: "month" }),
        [],
    );

    // If an already-subscribed user lands here (and we're not mid-verification),
    // send them straight to the dashboard.
    useEffect(() => {
        if (loading) return;
        if (!user) {
            navigate("/", { replace: true });
            return;
        }
        if (hasPaidAccess(user) && !sessionId) {
            navigate(nextRouteForUser(user), { replace: true });
        }
    }, [user, loading, sessionId, navigate]);

    // Friendly toast when returning from a canceled checkout.
    useEffect(() => {
        if (canceled) {
            toast.info("Checkout canceled — you can subscribe whenever you're ready.");
            setSearchParams({}, { replace: true });
            setPhase("idle");
        }
    }, [canceled, setSearchParams]);

    // Poll payment status after Stripe redirects back with a session_id.
    useEffect(() => {
        if (!sessionId) return undefined;
        const state = pollRef.current;
        state.attempts = 0;
        state.stopped = false;
        setPhase("verifying");

        const poll = async () => {
            if (state.stopped) return;
            state.attempts += 1;
            try {
                const { data } = await api.get(
                    `/stripe/checkout/status/${sessionId}`,
                );
                if (data.active) {
                    state.stopped = true;
                    const fresh = await refreshUser();
                    setPhase("success");
                    toast.success("Subscription active — welcome to ReelFlo!");
                    setTimeout(
                        () =>
                            navigate(nextRouteForUser(fresh || data.user), {
                                replace: true,
                            }),
                        1400,
                    );
                    return;
                }
                if (data.checkout_status === "expired") {
                    state.stopped = true;
                    setErrorMsg("This checkout session expired. Please try again.");
                    setPhase("error");
                    return;
                }
            } catch (e) {
                // Keep polling on transient errors until attempts run out.
            }
            if (state.attempts >= POLL_MAX_ATTEMPTS) {
                state.stopped = true;
                setErrorMsg(
                    "We couldn't confirm your payment yet. If you were charged, refresh in a moment.",
                );
                setPhase("error");
                return;
            }
            setTimeout(poll, POLL_INTERVAL_MS);
        };
        poll();
        return () => {
            state.stopped = true;
        };
    }, [sessionId, navigate, refreshUser]);

    const startCheckout = async () => {
        setErrorMsg("");
        setPhase("subscribing");
        try {
            const { data } = await api.post("/stripe/checkout/session", {
                origin_url: window.location.origin,
            });
            if (data.already_active) {
                const fresh = await refreshUser();
                navigate(nextRouteForUser(fresh), { replace: true });
                return;
            }
            if (data.url) {
                window.location.href = data.url;
                return;
            }
            throw new Error("No checkout URL returned");
        } catch (err) {
            setErrorMsg(
                err?.response?.data?.detail ||
                    "Could not start checkout. Please try again.",
            );
            setPhase("error");
        }
    };

    const retry = () => {
        pollRef.current.stopped = true;
        setErrorMsg("");
        setPhase("idle");
        setSearchParams({}, { replace: true });
    };

    const verifying = phase === "verifying";
    const success = phase === "success";
    const subscribing = phase === "subscribing";

    return (
        <div className="relative min-h-screen overflow-hidden bg-black text-white">
            <NeonBackground intensity={0.6} />

            {/* Top bar */}
            <header className="relative z-20 mx-auto flex w-full max-w-6xl items-center justify-between px-4 py-5 sm:px-6">
                <NeonLogo onClick={() => navigate("/")} />
                <div className="flex items-center gap-2 sm:gap-3">
                    <button
                        type="button"
                        data-testid="subscribe-account-link"
                        onClick={() => navigate("/account")}
                        className="inline-flex min-h-11 items-center gap-1.5 whitespace-nowrap rounded-full border border-white/15 px-2 py-2 text-sm text-white/70 transition-colors hover:border-white/30 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70 sm:gap-2 sm:px-4"
                    >
                        <UserCircle className="h-4 w-4" /> Account
                    </button>
                    <button
                        type="button"
                        data-testid="subscribe-logout-button"
                        onClick={() => {
                            logout();
                            navigate("/");
                        }}
                        className="inline-flex min-h-11 items-center gap-1.5 whitespace-nowrap rounded-full border border-white/15 px-2 py-2 text-sm text-white/70 transition-colors hover:border-white/30 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70 sm:gap-2 sm:px-4"
                    >
                        <LogOut className="h-4 w-4" /> Log out
                    </button>
                </div>
            </header>

            <main className="relative z-10 mx-auto flex w-full max-w-2xl flex-col px-4 py-6 sm:px-6 sm:py-10">
                <div className="mb-8">
                    <span className="inline-flex items-center gap-2 rounded-full border border-purple-400/30 bg-purple-500/10 px-4 py-1.5 text-sm font-medium text-purple-200">
                        <Sparkles className="h-4 w-4" /> One step left
                    </span>
                    <h1 className="rf-display mt-4 text-4xl uppercase leading-[1.05] sm:text-5xl">
                        Activate your ReelFlo subscription
                    </h1>
                    <p className="mt-3 text-lg text-white/55">
                        Unlock your content command center. Cancel anytime.
                    </p>
                </div>

                {/* Single plan panel — one clear pricing hierarchy, one dominant action */}
                <motion.section
                    data-testid="subscribe-plan-card"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="rounded-lg border border-white/12 bg-surface-panel p-7 sm:p-9"
                >
                    <div className="flex flex-col items-start gap-3 min-[360px]:flex-row min-[360px]:items-baseline min-[360px]:justify-between min-[360px]:gap-4">
                        <div>
                            <p className="text-base font-medium text-white/55">
                                ReelFlo
                            </p>
                            <div className="mt-1 flex items-end gap-1.5">
                                <span
                                    data-testid="subscribe-price"
                                    className="rf-display text-5xl sm:text-6xl"
                                >
                                    ${config.amount}
                                </span>
                                <span className="pb-2 text-lg text-white/50">
                                    /month
                                </span>
                            </div>
                        </div>
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold text-white/70">
                            <ShieldCheck className="h-4 w-4 text-emerald-300" /> Secure checkout
                        </span>
                    </div>

                    <div className="my-7 h-px w-full bg-white/10" />

                    <p className="mb-4 text-sm font-semibold uppercase tracking-wide text-white/45">
                        Everything done for you
                    </p>
                    <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {PERKS.map((perk) => (
                            <li
                                key={perk}
                                className="flex items-center gap-2.5 text-[15px] text-white/80"
                            >
                                <Check
                                    className="h-4 w-4 flex-none text-purple-300"
                                    strokeWidth={3}
                                />
                                {perk}
                            </li>
                        ))}
                    </ul>

                    <div className="my-7 h-px w-full bg-white/10" />

                    {success ? (
                        <div
                            data-testid="subscribe-success-state"
                            className="flex flex-col items-center justify-center py-6 text-center"
                        >
                            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15">
                                <PartyPopper className="h-8 w-8 text-emerald-300" />
                            </span>
                            <p className="rf-heading mt-5 text-2xl font-bold text-emerald-200">
                                You're all set!
                            </p>
                            <p className="mt-2 text-white/55">
                                Taking you to your dashboard…
                            </p>
                            <Loader2 className="mt-4 h-5 w-5 animate-spin text-white/40" />
                        </div>
                    ) : verifying ? (
                        <div
                            data-testid="subscribe-verifying-state"
                            className="flex flex-col items-center justify-center py-6 text-center"
                        >
                            <Loader2 className="h-10 w-10 animate-spin text-purple-300" />
                            <p className="rf-heading mt-5 text-xl font-bold">
                                Confirming your payment…
                            </p>
                            <p className="mt-2 text-sm text-white/50">
                                This only takes a few seconds.
                            </p>
                        </div>
                    ) : (
                        <>
                            <div>
                                <p className="rf-heading text-xl font-bold">
                                    Subscribe to continue
                                </p>
                                <p className="mt-2 text-sm leading-relaxed text-white/55">
                                    You'll be redirected to Stripe's secure
                                    checkout to enter your card. Your payment
                                    details never touch our servers.
                                </p>

                                <div className="mt-5 space-y-2.5 text-sm text-white/65">
                                    <p className="flex items-center gap-2">
                                        <ShieldCheck className="h-4 w-4 text-emerald-300" />
                                        Secure, PCI-compliant checkout
                                    </p>
                                    <p className="flex items-center gap-2">
                                        <CreditCard className="h-4 w-4 text-white/50" />
                                        $299 billed monthly · cancel anytime
                                    </p>
                                </div>

                                {phase === "error" && errorMsg && (
                                    <div
                                        data-testid="subscribe-error"
                                        className="mt-5 rounded-lg border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200"
                                    >
                                        {errorMsg}
                                    </div>
                                )}
                            </div>

                            {phase === "error" ? (
                                <NeonButton
                                    size="block"
                                    pulse={false}
                                    data-testid="subscribe-retry-button"
                                    onClick={retry}
                                    className="mt-6"
                                >
                                    <CreditCard className="h-5 w-5" /> Try again
                                </NeonButton>
                            ) : (
                                <NeonButton
                                    size="block"
                                    data-testid="subscribe-button"
                                    onClick={startCheckout}
                                    disabled={subscribing}
                                    className="mt-6"
                                >
                                    {subscribing ? (
                                        <Loader2 className="h-5 w-5 animate-spin" />
                                    ) : (
                                        <CreditCard className="h-5 w-5" />
                                    )}
                                    {subscribing
                                        ? "Redirecting…"
                                        : `Subscribe · $${config.amount}/mo`}
                                </NeonButton>
                            )}

                            <p className="mt-4 text-center text-xs text-white/35">
                                Powered by Stripe
                            </p>
                        </>
                    )}
                </motion.section>
            </main>
        </div>
    );
}
